import { GetDeviceHistoryBetweenTwoDatesResponseItem } from "../../../models/response/deviceDataHistory/GetDeviceHistoryBetweenTwoDatesResponse";
import moment from "moment";

/**
 * Araç takip animasyonları (07.10.2026, Burak onaylı mockup: D:\Keylogx_Canlı\mockup\arac-takip-animasyon.html).
 * 1) Geçmiş izleme: rota hıza göre renkli; üstünde beyaz noktalar gidiş yönüne akar, araç hızlandıkça akış hızlanır.
 * 2) Canlı konum: yön ikonunun çevresinde ikon renginde yayılan sinyal dalgası.
 */

/** Hız bantları (km/s). Eşikler Burak onaylı: 0-20 / 20-50 / 50-80 / 80-90 / 90+. */
export const HIZ_BANTLARI = [
    { ust: 20, renk: '#d93025', ad: 'dur-kalk' },
    { ust: 50, renk: '#f29900', ad: 'yavaş' },
    { ust: 80, renk: '#34a853', ad: 'normal' },
    { ust: 90, renk: '#0b8043', ad: 'hızlı' },
    { ust: Number.MAX_VALUE, renk: '#1a56db', ad: 'hız sınırı üstü' },
];

export const hizBandi = (kmh: number) => HIZ_BANTLARI.findIndex(x => kmh < x.ust);

const DESEN_PX = 18; // akan nokta aralığı
const ADIM_MS = 50;

const hareketAzalt = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Geçmiş rotasını çizer. Aynı hız bandındaki ardışık noktalar tek parça olur (nokta başına çizgi yerine).
 * Dönen nesnelerin hepsi setMap(null) ile kaldırılır; akış zamanlayıcısı çizgiler haritadan kalkınca kendiliğinden durur.
 */
export function rotaCiz(map: google.maps.Map, items: GetDeviceHistoryBetweenTwoDatesResponseItem[]): Array<google.maps.Polyline | google.maps.Marker> {
    const sonuc: Array<google.maps.Polyline | google.maps.Marker> = [];

    if (!items || items.length < 2)
        return sonuc;

    // Hız bandına göre parçalar
    type Parca = { bant: number, noktalar: GetDeviceHistoryBetweenTwoDatesResponseItem[], toplam: number, adet: number };
    const parcalar: Parca[] = [];

    for (let i = 1; i < items.length; i++) {
        const kmh = ((items[i - 1].kmPerHour ?? 0) + (items[i].kmPerHour ?? 0)) / 2;
        const bant = hizBandi(kmh);
        const son = parcalar[parcalar.length - 1];

        if (son && son.bant === bant) {
            son.noktalar.push(items[i]);
            son.toplam += kmh;
            son.adet++;
        }
        else
            parcalar.push({ bant, noktalar: [items[i - 1], items[i]], toplam: kmh, adet: 1 });
    }

    const yol = (p: Parca) => p.noktalar.map(x => new google.maps.LatLng(x.latitude, x.longitude));
    // gpsDateTime sunucudan "01.09.2026 08:12" biçiminde gelir
    const saat = (x: GetDeviceHistoryBetweenTwoDatesResponseItem) => {
        const m = moment(x.gpsDateTime, ['DD.MM.YYYY HH:mm:ss', 'DD.MM.YYYY HH:mm', moment.ISO_8601], true);
        return m.isValid() ? m.format('HH:mm') : (x.gpsDateTime ?? '');
    };
    const bilgi = new google.maps.InfoWindow({ disableAutoPan: true });

    // 1) Beyaz kenar: her zeminde (harita/uydu) okunur
    parcalar.forEach(p => sonuc.push(new google.maps.Polyline({
        path: yol(p), map, strokeColor: '#ffffff', strokeOpacity: 0.9, strokeWeight: 10, zIndex: 1, clickable: false,
    })));

    // 2) Hız rengi; üzerine gelince saat aralığı + ortalama hız
    parcalar.forEach(p => {
        const ort = Math.round(p.toplam / p.adet);
        const cizgi = new google.maps.Polyline({
            path: yol(p), map, strokeColor: HIZ_BANTLARI[p.bant].renk, strokeOpacity: 1, strokeWeight: 6, zIndex: 2,
        });

        cizgi.addListener('mouseover', (e: google.maps.PolyMouseEvent) => {
            bilgi.setContent(`${saat(p.noktalar[0])} - ${saat(p.noktalar[p.noktalar.length - 1])} · ort. ${ort} km/s (${HIZ_BANTLARI[p.bant].ad})`);
            bilgi.setPosition(e.latLng);
            bilgi.open(map);
        });
        cizgi.addListener('mouseout', () => bilgi.close());

        sonuc.push(cizgi);
    });

    // 3) Gidiş yönüne akan noktalar (durduğu yerde akış yok)
    const akanlar: { cizgi: google.maps.Polyline, hiz: number, ofset: number }[] = [];

    parcalar.forEach(p => {
        const ort = p.toplam / p.adet;

        if (ort < 1)
            return;

        const cizgi = new google.maps.Polyline({
            path: yol(p), map, strokeOpacity: 0, zIndex: 3, clickable: false,
            icons: [{
                icon: { path: 'M 0,-1 0,1', strokeColor: '#ffffff', strokeOpacity: 0.95, strokeWeight: 3, scale: 2 },
                offset: '0px',
                repeat: DESEN_PX + 'px',
            }],
        });

        // 100 km/s ~ 65 px/sn, 10 km/s ~ 12 px/sn
        const pxSn = Math.max(12, Math.min(65, ort * 0.65));
        akanlar.push({ cizgi, hiz: pxSn * ADIM_MS / 1000, ofset: 0 });
        sonuc.push(cizgi);
    });

    if (akanlar.length > 0 && !hareketAzalt()) {
        const zamanlayici = window.setInterval(() => {
            // Çizgiler haritadan kaldırıldıysa (yeni sorgu, geri dönüş) dur
            if (!akanlar[0].cizgi.getMap()) {
                window.clearInterval(zamanlayici);
                return;
            }

            akanlar.forEach(a => {
                a.ofset = (a.ofset + a.hiz) % DESEN_PX;
                const icons = a.cizgi.get('icons');
                icons[0].offset = a.ofset.toFixed(1) + 'px';
                a.cizgi.set('icons', icons);
            });
        }, ADIM_MS);
    }

    // Başlangıç / bitiş etiketleri
    const etiket = (x: GetDeviceHistoryBetweenTwoDatesResponseItem, metin: string, renk: string, sola: boolean) => new google.maps.Marker({
        map, position: new google.maps.LatLng(x.latitude, x.longitude), clickable: false, zIndex: 4,
        icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="22"><rect x="1" y="1" width="118" height="20" rx="10" fill="#fff" stroke="${renk}" stroke-width="2"/>` +
                `<text x="60" y="15" font-family="Inter, Segoe UI, Arial" font-size="11" font-weight="700" text-anchor="middle" fill="#1f2430">${metin}</text></svg>`),
            scaledSize: new google.maps.Size(120, 22),
            anchor: new google.maps.Point(sola ? 128 : -8, 28),
        },
    });

    sonuc.push(etiket(items[0], `Başlangıç ${saat(items[0])}`, HIZ_BANTLARI[2].renk, false));
    sonuc.push(etiket(items[items.length - 1], `Bitiş ${saat(items[items.length - 1])}`, '#374151', true));

    return sonuc;
}

/** Durum rengine göre dalga hızı; "Veri Alınamıyor" (black) aracında dalga yok (sinyal yokken sinyal animasyonu yanıltıcı). */
const DALGA_SURESI: Record<string, string> = { green: '1.6s', orange: '2.4s', purple: '2.4s', red: '3.4s' };

/** Canlı konum etiketine eklenen sinyal dalgası (etiket sol üstüne göre yön ikonunun merkezinde, labelAnchor -40,-95). */
export function sinyalDalgasiHtml(statusColor: string) {
    const sure = DALGA_SURESI[statusColor];

    if (!sure)
        return '';

    return `<div class="sinyal-dalgasi" style="--dalga-renk:${statusColor};--dalga-sure:${sure}"></div>`;
}
